import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

const CARDS = [
  {
    emoji: "🚀",
    title: "Zero Friction",
    description: "Users express intent in plain language. No forms, no workflows to design — just describe what you need.",
    color: COLORS.neonGreen,
    startFrame: 30,
  },
  {
    emoji: "🔌",
    title: "No Feature APIs",
    description: "Capabilities are discovered and composed dynamically. The platform adapts to the task, not the other way around.",
    color: COLORS.neonBlue,
    startFrame: 50,
  },
  {
    emoji: "🛠️",
    title: "User-Defined Everything",
    description: "Agents, tools, and workflows are defined by users — not hardcoded by engineers. The system grows with your needs.",
    color: COLORS.neonOrange,
    startFrame: 70,
  },
  {
    emoji: "🤖",
    title: "Autonomous Execution",
    description: "Multi-step tasks run end-to-end without supervision. Monitor, adapt, and report — all without human intervention.",
    color: COLORS.neonPurple,
    startFrame: 90,
  },
];

const BG_ORBS = [
  { x: 150,  y: 150,  size: 500, color: COLORS.neonGreen,  freq: 0.015, phase: 0   },
  { x: 1700, y: 200,  size: 420, color: COLORS.neonBlue,   freq: 0.02,  phase: 1.5 },
  { x: 200,  y: 850,  size: 380, color: COLORS.neonOrange, freq: 0.018, phase: 3   },
  { x: 1750, y: 900,  size: 460, color: COLORS.neonPurple, freq: 0.012, phase: 2   },
];

export const Scene03Principles: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.gentle });

  const exitOpacity = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* Background drifting orbs */}
      <AbsoluteFill style={{ pointerEvents: "none" }}>
        {BG_ORBS.map((orb, i) => {
          const driftX = Math.sin(frame * orb.freq + orb.phase) * 30;
          const driftY = Math.cos(frame * orb.freq * 0.8 + orb.phase) * 20;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: orb.x + driftX - orb.size / 2,
                top: orb.y + driftY - orb.size / 2,
                width: orb.size,
                height: orb.size,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${orb.color}08 0%, transparent 70%)`,
              }}
            />
          );
        })}
      </AbsoluteFill>

      {/* Parallax depth layers */}
      <div style={{
        position: "absolute",
        inset: 0,
        transform: `translateY(${Math.sin(frame * 0.008) * 8}px) translateX(${Math.cos(frame * 0.006) * 5}px)`,
        pointerEvents: "none",
      }}>
        {[
          { x: 150, y: 200, size: 400, color: COLORS.neonGreen, speed: 0.005, phase: 0 },
          { x: 1600, y: 300, size: 350, color: COLORS.neonBlue, speed: 0.004, phase: 2 },
          { x: 900, y: 800, size: 500, color: COLORS.neonOrange, speed: 0.003, phase: 4 },
        ].map((orb, i) => (
          <div key={`parallax-${i}`} style={{
            position: "absolute",
            left: orb.x + Math.sin(frame * orb.speed + orb.phase) * 30 - orb.size / 2,
            top: orb.y + Math.cos(frame * orb.speed * 0.7 + orb.phase) * 20 - orb.size / 2,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${orb.color}06 0%, transparent 70%)`,
          }} />
        ))}
      </div>

      {/* Mid-layer floating particles */}
      {[...Array(6)].map((_, i) => {
        const px = 200 + i * 300;
        const py = 150 + (i % 3) * 300;
        const pColor = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan][i];
        return (
          <div key={`particle-mid-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.5) * 15,
            top: py + Math.cos(frame * 0.015 + i * 2) * 12,
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            borderRadius: "50%",
            background: pColor,
            boxShadow: `0 0 8px ${pColor}80, 0 0 16px ${pColor}40`,
            opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.2,
            pointerEvents: "none",
          }} />
        );
      })}

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 55,
          left: 0,
          right: 0,
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          transform: `translateY(${interpolate(titleAppear, [0, 1], [-20, 0])}px)`,
        }}
      >
        <span
          style={{
            fontSize: 48,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            background: `linear-gradient(135deg, ${COLORS.neonBlue}, ${COLORS.neonPurple})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            filter: `drop-shadow(0 0 20px ${COLORS.neonBlue}50)`,
          }}
        >
          Core Principles
        </span>
      </div>

      {/* 2x2 Card grid */}
      <div
        style={{
          position: "absolute",
          top: 155,
          left: 100,
          right: 100,
          bottom: 80,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: 36,
        }}
      >
        {CARDS.map((card, i) => {
          const cardSpring = spring({
            frame: frame - card.startFrame,
            fps,
            config: SPRING.bouncy,
          });
          const opacity = interpolate(cardSpring, [0, 1], [0, 1]);
          const translateY = interpolate(cardSpring, [0, 1], [40, 0]);
          const scale = interpolate(cardSpring, [0, 1], [0.85, 1]);

          // Gentle oscillating Y rotation
          const oscillateY = Math.sin(frame * 0.02 + i * 0.8) * 3;
          const oscillateX = Math.cos(frame * 0.015 + i * 0.6) * 1.5;

          return (
            <div
              key={i}
              style={{
                perspective: "800px",
                opacity,
                transform: `translateY(${translateY}px) scale(${scale})`,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: COLORS.bgCard,
                  borderRadius: 20,
                  borderTop: `4px solid ${card.color}`,
                  border: `1px solid ${card.color}30`,
                  borderTopWidth: 4,
                  borderTopColor: card.color,
                  padding: "36px 36px 32px",
                  boxSizing: "border-box",
                  transform: `rotateY(${oscillateY}deg) rotateX(${oscillateX}deg) translateZ(${Math.sin(frame * 0.01) * 3}px)`,
                  boxShadow: `0 0 30px ${card.color}30, 0 0 60px ${card.color}15, inset 0 0 30px ${card.color}05`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                {/* Emoji */}
                <div
                  style={{
                    fontSize: 48,
                    lineHeight: 1,
                    filter: `drop-shadow(0 0 12px ${card.color}80)`,
                  }}
                >
                  {card.emoji}
                </div>

                {/* Title */}
                <div
                  style={{
                    fontSize: 24,
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    color: COLORS.textPrimary,
                    textShadow: `0 0 15px ${card.color}50`,
                  }}
                >
                  {card.title}
                </div>

                {/* Description */}
                <div
                  style={{
                    fontSize: 16,
                    fontFamily: FONTS.body,
                    fontWeight: 400,
                    color: COLORS.textSecondary,
                    lineHeight: 1.65,
                    flex: 1,
                  }}
                >
                  {card.description}
                </div>

                {/* Bottom accent line */}
                <div
                  style={{
                    height: 2,
                    background: `linear-gradient(90deg, ${card.color}80, transparent)`,
                    borderRadius: 2,
                    width: `${interpolate(cardSpring, [0, 1], [0, 100])}%`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
